import type { FormikErrors } from 'formik'
import React from 'react'
import { VirtuosoGrid } from 'react-virtuoso'
import styled from 'styled-components'

import { SourceCard } from 'features/project-form/features/source/components'

interface SourceItem {
  id?: string | number
  source?: string
  title?: string
  name?: string
  product_id?: string | number
  [key: string]: unknown
}

interface FormikWithErrors {
  errors: FormikErrors<{ sources?: unknown }>
}

type Props = {
  items: SourceItem[]
  handleOpen: () => void
  setEditIndex: (n: number) => void
  formik: FormikWithErrors
}

const ProjectSourceVirtualizedList: React.FC<Props> = ({
  items,
  handleOpen,
  setEditIndex,
  formik,
}) => {
  const ProductSourceCard = ({
    item,
    index,
  }: {
    item: SourceItem
    index: number
  }): JSX.Element => {
    return (
      <>
        {item.source ? (
          <SourceCard
            error={!!formik.errors.sources && !item.product_id}
            icon={item.source === 'File' ? 'CSV' : 'URL'}
            title={item.title || ''}
            onClick={() => {
              handleOpen()
              setEditIndex(index)
            }}
          />
        ) : (
          <SourceCard
            icon={item.name?.[0] || '?'}
            title={item.name || ''}
            onClick={() => {
              handleOpen()
              setEditIndex(index)
            }}
          />
        )}
      </>
    )
  }

  const GridItem = ({
    children,
    ...props
  }: {
    children: { item: SourceItem; index: number }
  } & React.HTMLAttributes<HTMLDivElement>): JSX.Element => {
    const { item, index } = children
    if (!item) return <div>Loading</div>
    return (
      <SourceCardWrapper key={item.id} {...props}>
        <ProductSourceCard item={item} index={index} />
      </SourceCardWrapper>
    )
  }

  return (
    <VirtuosoGrid
      totalCount={items.length}
      overscan={200}
      components={{
        Item: GridItem as never,
        List: React.forwardRef<
          HTMLDivElement,
          React.HTMLAttributes<HTMLDivElement>
        >(function VirtuosoListContainer(
          { style, children, ...rest },
          listRef,
        ) {
          return (
            <div
              ref={listRef}
              {...rest}
              style={{
                ...(style as React.CSSProperties),
                display: 'flex',
                flexWrap: 'wrap',
              }}
            >
              {children}
            </div>
          )
        }) as never,
      }}
      itemContent={(index) =>
        ({
          item: items[index],
          index,
        }) as never
      }
      style={{ height: '300px', width: '100%' }}
    />
  )
}

export default ProjectSourceVirtualizedList

const SourceCardWrapper = styled.div`
  flex-basis: 40%;
  padding: 10px;
  display: flex;

  height: 150px;
  flex-grow: 1;

  @media screen and (max-width: 1024px) {
    width: 100%;
  }
`
